/**
 * Post-Visit Summary (PVS) — fetch + print hooks.
 *
 * Wire (Flutter `encounter_module/encounter_base/data/datasources/encounter_data_source_impl.dart:1154-1194`):
 *
 *   GET /api/Patients/PostVisitSummary?encounterId={id?}&appointmentId={id?}
 *       → PostVisitSummaryData | null (404 → null, Flutter catches)
 *
 *   GET /api/Patients/PVSPrint?encounterId={id?}&appointmentId={id?}
 *       → string (base64-encoded PDF per Flutter `post_visit_summary_bloc.dart:52`,
 *                 OR URL per the billing PrintInvoice precedent — format
 *                 pending dev curl; the print mutation handles both
 *                 defensively until then.)
 *
 * Scope: first encounter_module carve-out into PM (PROD-874). Per
 * orchestration log "Encounter Carve-Out Model (Plan B)" — opt-in
 * per-feature, not a full encounter port.
 */

import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PostVisitSummaryData } from '@/types/post-visit-summary';

export interface PvsLookup {
  encounterId?: string | null;
  appointmentId?: string | null;
}

/**
 * Build the query string with the same conditional inclusion as Flutter
 * `:1182-1185` — only ship the key if its value is truthy.
 */
function buildPvsQuery(p: PvsLookup): string {
  const qs = new URLSearchParams();
  if (p.encounterId) qs.set('encounterId', p.encounterId);
  if (p.appointmentId) qs.set('appointmentId', p.appointmentId);
  return qs.toString();
}

/**
 * Fetch the PVS payload. Enabled only when at least one identifier is
 * present; disabled queries return `data: undefined` and never fire.
 */
export function usePostVisitSummary(
  params: PvsLookup,
): UseQueryResult<PostVisitSummaryData | null> {
  return useQuery({
    queryKey: [
      'post-visit-summary',
      params.encounterId ?? null,
      params.appointmentId ?? null,
    ],
    queryFn: async ({ signal }) => {
      const qs = buildPvsQuery(params);
      const raw = await get<PostVisitSummaryData | null>(
        `/api/Patients/PostVisitSummary?${qs}`,
        { signal },
      );
      return raw ?? null;
    },
    enabled: !!(params.encounterId || params.appointmentId),
    staleTime: 60_000,
  });
}

/**
 * Strip the wrapping `"…"` characters that .NET sometimes wraps around a
 * bare-string response. Same pattern as
 * `src/billing/services/use-invoice-actions.ts:37-39`.
 */
function stripQuotes(raw: string): string {
  return raw.replace(/^"+|"+$/g, '');
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

/**
 * Decode a base64 string into a `Uint8Array<ArrayBuffer>` (concrete
 * ArrayBuffer-backed, not the generic `ArrayBufferLike` variant that
 * `new Uint8Array(length)` returns under the stricter `lib.es2024.*`
 * typings). The concrete buffer type is what `BlobPart` requires, so we
 * allocate the buffer explicitly and write through a view.
 */
function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const buffer = new ArrayBuffer(binary.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * Open the PVS print response in a new browser tab.
 *
 * Defensive — supports either response format until dev curl resolves
 * which the backend actually returns:
 *   - HTTP URL string  → `window.open(url, …)` directly (billing-print pattern)
 *   - base64 PDF bytes → blob URL → `window.open` (Flutter pattern, see
 *                                  `post_visit_summary_bloc.dart:52`)
 *
 * The object URL is revoked after 60s — by then the browser's PDF
 * viewer has loaded the bytes, and the URL is no longer needed. Matches
 * the cleanup cadence used elsewhere for transient blob URLs.
 */
export function openPvsPrintResponse(raw: string): void {
  const value = stripQuotes(raw.trim());
  if (!value) {
    throw new Error('PVSPrint: server returned an empty response.');
  }

  if (isHttpUrl(value)) {
    window.open(value, '_blank', 'noopener,noreferrer');
    return;
  }

  // Treat as base64 PDF bytes. `atob` throws on malformed input — the
  // mutation's onError surfaces that via the global toast.
  const bytes = base64ToBytes(value);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Print/Download the PVS PDF. Mutation rather than query so the request
 * fires only on user click, matches the billing `usePrintInvoice` shape.
 */
export function usePVSPrint() {
  return useMutation({
    mutationFn: async (params: PvsLookup) => {
      if (!params.encounterId && !params.appointmentId) {
        throw new Error(
          'PVSPrint: at least one of encounterId / appointmentId is required.',
        );
      }
      const qs = buildPvsQuery(params);
      const raw = await get<string>(`/api/Patients/PVSPrint?${qs}`);
      openPvsPrintResponse(typeof raw === 'string' ? raw : String(raw));
      return true;
    },
  });
}
